/**
 * AnalyticsFeatureFlag
 *
 * Resuelve el modo de enrutamiento de analítica ('sdk' | 'rest' | 'none') a partir
 * de los metadatos APA y la plataforma del dispositivo actual.
 *
 * Reglas de enrutamiento:
 *   - Metadata inválida / faltante / malformada          → 'sdk'
 *   - Dispositivo no en targetDevices                    → 'sdk'
 *   - rest=false, sdkDisabled=false                      → 'sdk'
 *   - rest=true,  sdkDisabled=false                      → 'sdk'
 *   - rest=true,  sdkDisabled=true                       → 'rest'
 *   - rest=false, sdkDisabled=true                       → 'none'
 */

import Metadata from '../../requests/apa/Metadata';
import Device from '../../devices/device';

/** Clave APA para el flag de activación del cliente REST de GA4. */
const KEY_REST = 'feature_analytics_rest_enabled';

/** Clave APA para el flag de desactivación del SDK de analítica. */
const KEY_SDK_DISABLED = 'feature_analytics_sdk_disabled';

/** Modo por defecto cuando no hay datos suficientes para decidir. */
const MODE_SDK = 'sdk';
const MODE_REST = 'rest';
const MODE_NONE = 'none';

/**
 * Valída que un objeto de metadata tiene la forma esperada:
 * un objeto con la propiedad targetDevices como array.
 *
 * @param {any} meta - Valor retornado por Metadata.get()
 * @returns {boolean}
 */
function isValidMeta(meta) {
    return (
        meta !== null &&
        meta !== undefined &&
        typeof meta === 'object' &&
        !Array.isArray(meta) &&
        Array.isArray(meta.targetDevices)
    );
}

/**
 * Retorna true si la plataforma del dispositivo actual está incluida
 * en el array targetDevices de la metadata.
 *
 * Usa Device.getDevice().platform — NO getDeviceType(), porque los valores
 * en targetDevices son 'ps4', 'tizen', etc., no 'console'.
 *
 * @param {string[]} targetDevices - Lista de plataformas objetivo
 * @returns {boolean}
 */
function isCurrentDeviceTargeted(targetDevices) {
    const device = Device.getDevice();
    const platform = device && device.platform;
    if (!platform) return false;
    return targetDevices.indexOf(platform) !== -1;
}

/**
 * Resuelve el modo de analítica a partir de los metadatos APA.
 * En caso de cualquier error o dato inválido, retorna 'sdk' como fallback seguro.
 *
 * @returns {'sdk'|'rest'|'none'}
 */
export function getCurrentAnalyticsMode() {
    try {
        const restMeta = JSON.parse(Metadata.get(KEY_REST));
        const sdkMeta = JSON.parse(Metadata.get(KEY_SDK_DISABLED));

        // Si alguno de los metadatos es inválido, retornar sdk
        if (!isValidMeta(restMeta) || !isValidMeta(sdkMeta)) {
            return MODE_SDK;
        }

        // Si el dispositivo no está en los targets de alguna de las claves, retornar sdk
        if (
            !isCurrentDeviceTargeted(restMeta.targetDevices) ||
            !isCurrentDeviceTargeted(sdkMeta.targetDevices)
        ) {
            return MODE_SDK;
        }

        const restEnabled = Boolean(restMeta.enabled);
        const sdkDisabled = Boolean(sdkMeta.enabled);

        // Aplicar la matriz de enrutamiento
        if (restEnabled && sdkDisabled) return MODE_REST;
        if (!restEnabled && sdkDisabled) return MODE_NONE;

        // rest=true sdkDisabled=false → sdk
        // rest=false sdkDisabled=false → sdk
        return MODE_SDK;

    } catch (e) {
        // Fallback seguro ante cualquier error inesperado
        return MODE_SDK;
    }
}

/**
 * Indica si la analítica debe usar el cliente REST (Measurement Protocol).
 *
 * @returns {boolean} true solo cuando el modo resuelto es 'rest'
 */
export function isAnalyticsRestEnabled() {
    return getCurrentAnalyticsMode() === MODE_REST;
}

/**
 * Indica si el SDK de analítica debe desactivarse para el dispositivo actual.
 *
 * @returns {boolean} true cuando el modo resuelto es 'rest' o 'none'
 */
export function isAnalyticsSdkDisabled() {
    const mode = getCurrentAnalyticsMode();
    return mode === MODE_REST || mode === MODE_NONE;
}



// WEBPACK FOOTER //
// ./src/utils/trackers/AnalyticsFeatureFlag.js